import { useEffect, useState } from "react"
import { useSelector, useDispatch } from "react-redux"
import { useTranslation } from "react-i18next"

import wsClient from "src/wsClient"
import ModalService from "src/modals"
import {
  selectSelectedNodes,
  selectMovementIntent,
  selectMovementTroops,
  resetMovementNodes,
  cancelMovement,
  setMovementTroops,
} from "src/store/ui"
import {
  selectCityUnitsForPlayer,
  selectCurrentPlayer,
  selectValidUnitsForPlayer,
  selectCurrentTeam,
  selectGame,
  selectIsCurrentPlayerReady,
  selectPlayerFood,
  selectResearchLevels,
} from "src/store/game"
import { selectGameConfig } from "src/store/common"
import { cellDisplayName, expandNodesToPath } from "src/utils/maps"
import { etaForPath, speedOfStack } from "src/utils/movementEta"
import { consumptionMultiplier } from "src/utils/gameModifiers"
import { foodIcon } from "src/utils/resources"

import Modal from "src/components/modals/Modal"
import UnitPicker, { takesText } from "src/components/UnitPicker"

import "./AttackModal.css"

const troopConfigFor = (gameConfig, team) =>
  gameConfig?.[team === "human" ? "human_config" : "zombie_config"]?.TROOPS || {}

/* The provisions table (owner, 2026-09-29): one row per round of travel, rounds 1 to `rows` — how
   far the stack gets over land in that many rounds and what the trip prepays, one round of upkeep
   per round (`move_troops`, Q13c) — plus the drawn route's own row when it is longer than that. */
export const provisionRows = ({ speed, landCost = 1, upkeepPerRound, food, eta, rows = 5 }) => {
  if (!speed || !upkeepPerRound) return []
  const lengths = Array.from({ length: rows }, (_, index) => index + 1)
  if (eta > rows) lengths.push(eta)
  return lengths.map((rounds) => {
    const cost = upkeepPerRound * rounds
    return {
      rounds,
      cells: Math.floor((rounds * speed) / landCost),
      food: cost,
      affordable: cost <= food,
      isRoute: rounds === eta,
    }
  })
}

// Space's test lives with the picker it is about; re-exported for the tests that pin it here.
export { takesText }

/* Every unit standing here, workers aside: what the picker opens with. */
export const defaultForce = (availableUnits, troopConfig) =>
  Object.fromEntries(
    availableUnits
      .filter(({ type, available }) => available > 0 && troopConfig[type]?.ROLE !== "population")
      .map(({ type, available }) => [type, available])
  )

const AttackModal = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  // The draft force lives in the store (Q2): the picker closes while the route is drawn on the
  // map, and the route step needs the stack to say how many rounds the trip takes. Local state
  // stays for snappy slider dragging; every change is mirrored to the store.
  const storedTroops = useSelector(selectMovementTroops)
  // Back to the map to draw again: the modal can't be clicked through, so it steps aside.
  const resetPath = () => {
    dispatch(resetMovementNodes())
    ModalService.hide()
  }
  const selectedNodes = useSelector(selectSelectedNodes)
  const isReady = useSelector(selectIsCurrentPlayerReady)
  // One action, two wordings: a colony expedition is an ordinary `move_troops` whose force was
  // picked for it, and only the copy here says what it is for. (Infiltration was the third
  // intent and was removed from the game on 2026-09-10.)
  const intent = useSelector(selectMovementIntent)
  const isColonization = intent === "colonize"

  // The origin is the *last* node: startMovementFromCity seeds it and every later click is
  // inserted before it.
  const origin = selectedNodes.length ? selectedNodes[selectedNodes.length - 1] : null
  const unitsHere = useSelector((state) =>
    origin ? selectCityUnitsForPlayer(state, { col: origin.col, row: origin.row }) : []
  )
  // Every unit the class can field, in the server's order, with what this city holds of each
  // (owner, 2026-09-07): the rows used to be the city's own units dict, so a town that had never
  // held a soldier listed a ranger and a tank at 0 and no soldier at all — the shape of the list
  // was the city's history rather than the army. Anything standing here the class cannot build
  // (a captured stack, say) still gets a row after them.
  const validUnits = useSelector(selectValidUnitsForPlayer)
  const hereByType = Object.fromEntries(unitsHere.map(({ type, available }) => [type, available]))
  const availableUnits = [
    ...validUnits.map((type) => ({ type, available: hereByType[type] ?? 0 })),
    ...unitsHere.filter(({ type, available }) => !validUnits.includes(type) && available > 0),
  ]

  const gameConfig = useSelector(selectGameConfig)
  const team = useSelector(selectCurrentTeam)
  const troopConfig = troopConfigFor(gameConfig, team)

  /* The picker opens with the whole army already chosen (owner, 2026-09-29): sending everything
     is the common case, and a march is trimmed down more often than built up. Never the workers —
     marching a city's farmhands out is a choice nobody makes by default (the same line "100%"
     holds, P16). A force already drafted — reopened from the route bar, or a colony party — is
     kept as it is. */
  const [selectedTroops, setSelectedTroops] = useState(() =>
    Object.values(storedTroops || {}).some((number) => Number(number) > 0)
      ? storedTroops
      : defaultForce(availableUnits, troopConfig)
  )
  // Mirrored in an effect, not inside the updater: a dispatch during the setState updater runs
  // in React's render phase and warns "cannot update RoutePlanner while rendering AttackModal".
  useEffect(() => {
    dispatch(setMovementTroops(selectedTroops))
  }, [selectedTroops, dispatch])

  const path = expandNodesToPath(selectedNodes)

  const game = useSelector(selectGame)
  const researchLevels = useSelector(selectResearchLevels)
  const race = useSelector(selectCurrentPlayer)?.race
  const food = useSelector(selectPlayerFood)

  const eta = etaForPath({ gameConfig, team, troops: selectedTroops, path, race, researchLevels })
  const currentTick = game?.tick ?? 0

  const chosen = Object.entries(selectedTroops).filter(([, number]) => Number(number) > 0)
  const totalUnits = chosen.reduce((total, [, number]) => total + Number(number), 0)

  const confirmMovement = () => {
    const troops = Object.fromEntries(chosen)
    if (!path.length || !Object.keys(troops).length) return

    const sent = wsClient.send("game", { action: "move_troops", path, troops })
    // The player drew this path and chose these troops. If the send never left — a dropped socket,
    // which `send` reports — keep both so they can try again once it is back, rather than throwing
    // the work away and closing as though the march were under way.
    if (!sent) return

    dispatch(cancelMovement())
    ModalService.hide()
  }

  const setUnitNumber = (type, number) =>
    setSelectedTroops((current) => ({ ...current, [type]: Math.max(0, Number(number) || 0) }))

  // Space carries the flow forward here too (owner's P13): with a force picked, it is the send
  // button — also straight after a slider was dragged or a count typed (owner, 2026-09-29): the
  // sliders and the digit-only count fields have no use for a space, so they do not keep it. Only
  // a field that takes text (the chat, say) does.
  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key !== " ") return
      if (takesText(document.activeElement)) return
      if (isReady || totalUnits === 0) return
      event.preventDefault()
      // With a force picked and no route yet, Space is "draw the route" (owner's R24): the same
      // step the button below offers, so the keyboard carries the whole flow.
      if (path.length < 2) {
        ModalService.hide()
        return
      }
      confirmMovement()
    }
    window.addEventListener("keydown", onKeyDown)
    return () => window.removeEventListener("keydown", onKeyDown)
  })

  // The three ways a player actually fills this in: everything, half of everything, nothing.
  // "Todo" fills the *army*, never the population (owner's P16): workers keep whatever the player
  // set by hand — marching a city's farmhands out is a choice nobody makes with a shortcut button.
  const fillAll = (fraction) =>
    setSelectedTroops((current) =>
      Object.fromEntries(
        availableUnits.map(({ type, available }) => [
          type,
          troopConfig[type]?.ROLE === "population" && fraction > 0
            ? Number(current[type]) || 0
            : Math.floor(available * fraction),
        ])
      )
    )

  // Nodes in the order the march walks them: origin first, target last. `selectedNodes` is stored
  // the other way round because every waypoint is inserted before the origin.
  const walkOrder = [...selectedNodes].reverse()

  const arrivalAt = (node) => {
    const prefix = expandNodesToPath([...selectedNodes.slice(selectedNodes.indexOf(node)), origin].filter(Boolean))
    const ticks = etaForPath({ gameConfig, team, troops: selectedTroops, path: prefix, race, researchLevels })
    return ticks == null ? null : currentTick + ticks
  }

  const consumptionMod = consumptionMultiplier({ gameConfig, race })
  // The trip is provisioned at departure: one round's upkeep per round of travel (Q13c), exactly as
  // `move_troops` bills it. The route is read off the store at render time, so the figure is the
  // same whichever the player picked first, the units or the path (owner's S20) — it used to
  // quote one round's upkeep and call it the trip.
  const upkeepPerRound = Math.trunc(
    chosen.reduce(
      (total, [unit, number]) => total + (troopConfig[unit]?.CONSUMPTION || 0) * Number(number),
      0
    ) * consumptionMod
  )
  const rows = provisionRows({
    speed: speedOfStack({ gameConfig, team, troops: selectedTroops, race, researchLevels }),
    landCost: gameConfig?.rules_config?.MOVEMENT_COST_LAND ?? 1,
    upkeepPerRound,
    food,
    eta,
  })

  // Units first, then the route (owner's Q2): the picker knows nothing the map needs, but the
  // route step needs the stack's pace to say how many rounds the trip takes.
  const step = totalUnits === 0 ? 1 : selectedNodes.length < 2 ? 2 : 3
  const steps = [
    { id: 1, label: t("attack.stepForce") },
    { id: 2, label: t("attack.stepPath") },
    { id: 3, label: t("attack.stepConfirm") },
  ]

  const originName = origin ? cellDisplayName({ row: origin.row, column: origin.col, held: true }) : ""

  /* `maxHeight` is the room this sheet actually has, not a share of the window: it hangs 4vh below
     the map's top edge, and 4vh + 90vh + the command bar is taller than any screen — at 1280x800
     the foot sat 7px under the bottom of the window, which is where CANCEL is. 100% is the
     wrapper's own box, which already has the bar and both pads taken out of it. */
  return (
    <Modal width="min(1040px, 96%)" isolated={false} scope="map" top="4vh" maxHeight="100%">
      <header className="march-head">
        <h1>
          {isColonization ? t("attack.colonyOrder") : t("attack.marchOrder")}
        </h1>
        <div className="march-steps">
          {steps.map((option) => (
            <span
              key={option.id}
              className={`march-step${step === option.id ? " active" : ""}${step > option.id ? " done" : ""}`}
            >
              <span className="march-step-mark">{step > option.id ? "✓" : option.id}</span>
              {option.label}
            </span>
          ))}
        </div>
        <span className={`march-kind march-kind-${isColonization ? "colonize" : "attack"}`}>
          {isColonization ? t("attack.colonize") : t("attack.attack")}
        </span>
      </header>

      <div className="march-path">
        <div className="march-path-nodes with-scroll-bar horizontal">
          {walkOrder.map((node, index) => {
            const isOrigin = index === 0
            const isTarget = index === walkOrder.length - 1 && walkOrder.length > 1
            const arrival = isOrigin ? null : arrivalAt(node)
            return (
              <div className="march-node-group" key={`${node.col}-${node.row}`}>
                {index > 0 && <span className={`march-leg${isTarget ? " hostile" : ""}`} />}
                <div className="march-node">
                  <span className={`march-node-chip${isOrigin ? " origin" : ""}${isTarget ? " target" : ""}`}>
                    {isOrigin ? "⌂" : isTarget ? "✕" : "·"}
                  </span>
                  <span className="march-node-coords">
                    {node.col}-{node.row}
                  </span>
                  <span className={`march-node-when${isTarget ? " hostile" : ""}`}>
                    {isOrigin
                      ? originName
                      : arrival == null
                      ? "—"
                      : isTarget
                      ? t("attack.arrivesRound", { round: arrival })
                      : t("attack.round", { round: arrival })}
                  </span>
                </div>
              </div>
            )
          })}
        </div>

        <div className="march-path-meta">
          <span className="march-path-figures">
            {eta != null ? `${t("attack.rounds", { count: eta })} · ` : ""}
            {t("attack.cells", { count: Math.max(0, path.length - 1) })}
          </span>
          <button className="quiet march-reset" onClick={resetPath}>
            {t("attack.resetPath")}
          </button>
        </div>
      </div>

      <div className="march-body">
        {/* The picker is `UnitPicker`, shared with the tunnel sheet: the first Tab lands on the
            first count and Tab walks the counts (`utils/countFields`). */}
        <UnitPicker
          className="march-force"
          label={t("attack.sendFrom", { origin: originName || t("attack.here") })}
          units={availableUnits}
          value={selectedTroops}
          onChange={setUnitNumber}
          onFill={fillAll}
          disabled={isReady}
          emptyText={t("attack.noTroopsHere")}
        />

        <section className="march-provisions">
          <div className="march-section-head">
            <span className="section-label">{t("attack.provisions")}</span>
            <span className="march-rule" />
          </div>
          {rows.length === 0 ? (
            <p className="march-empty">{t("attack.provisionsEmpty")}</p>
          ) : (
            <table className="march-provisions-table">
              <thead>
                <tr>
                  <th scope="col">{t("attack.colRounds")}</th>
                  <th scope="col">{t("attack.colCells")}</th>
                  <th scope="col">
                    {t("attack.colFood")} {foodIcon(team)}
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr
                    key={row.rounds}
                    className={`${row.affordable ? "good" : "bad"}${row.isRoute ? " route" : ""}`}
                    data-testid={`provision-${row.rounds}`}
                  >
                    <td>{row.rounds}</td>
                    <td>{row.cells}</td>
                    <td>{row.food}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <p className="march-provisions-why">{t("attack.provisionsWhy")}</p>
        </section>
      </div>

      <footer className="march-foot">
        {totalUnits > 0 && selectedNodes.length < 2 && (
          <button
            className="march-send"
            data-action="draw-route"
            onClick={() => ModalService.hide()}
            disabled={isReady}
          >
            {t("attack.drawRoute")} →
          </button>
        )}
        {!(totalUnits > 0 && selectedNodes.length < 2) && (
          <button
            className="march-send hostile"
            data-action="send-march"
            onClick={confirmMovement}
            disabled={isReady || totalUnits === 0 || path.length < 2}
          >
            {isReady
              ? t("attack.lockedIn")
              : totalUnits > 0
              ? t("attack.sendUnits", { count: totalUnits })
              : t("attack.pickYourForce")}
          </button>
        )}
        <button className="quiet march-back" onClick={() => ModalService.hide()}>
          ← {t("attack.backToMap")}
        </button>
        <button
          className="quiet march-cancel"
          onClick={() => {
            dispatch(cancelMovement())
            ModalService.hide()
          }}
        >
          {t("attack.cancel")}
        </button>
      </footer>
    </Modal>
  )
}

export default AttackModal
